import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import quizQuestions from '../quizQuestions';

const TimedQuizRandomScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Extract username and difficulty from location.state. Default difficulty to "Easy" if not provided.
  const { username, difficulty = "Easy" } = location.state || { username: 'Guest', difficulty: 'Easy' };

  // Filter questions by the chosen difficulty, shuffle them, and take 5.
  const [sessionQuestions] = useState(() => {
    const filtered = quizQuestions.filter(q => q.difficulty === difficulty);
    const shuffled = [...filtered].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 5);
  });

  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  // Instead of a fixed 30s, we'll have allowedTime and timer states.
  const [allowedTime, setAllowedTime] = useState(30);
  const [timer, setTimer] = useState(30);
  const [times, setTimes] = useState([]);

  // When a new question starts and the quiz is active,
  // generate a random allowed time between 15 and 60 seconds and set both allowedTime and timer.
  useEffect(() => {
    if (quizStarted) {
      const randomTime = Math.floor(Math.random() * (60 - 15 + 1)) + 15; // random integer between 15 and 60
      setAllowedTime(randomTime);
      setTimer(randomTime);
    }
  }, [currentQuestionIndex, quizStarted]);

  // Timer countdown effect.
  useEffect(() => {
    let interval = null;
    if (quizStarted && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (quizStarted && timer <= 0) {
      clearInterval(interval);
      alert(`Time's up! Your score: ${score}`);
      navigate('/');
    }
    return () => clearInterval(interval);
  }, [quizStarted, timer, score, navigate]);

  // Function to start the quiz.
  const handleStartQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setTimes([]);
  };

  // Handle answer selection.
  const handleAnswer = (option) => {
    if (selectedAnswer !== null) return; // Prevent double selection.
    setSelectedAnswer(option);
    const currentQuestion = sessionQuestions[currentQuestionIndex];
    if (option === currentQuestion.answer) {
      setScore(prev => prev + 1);
    }
    // Calculate time taken for this question.
    const timeTaken = allowedTime - timer;
    setTimes(prev => [...prev, timeTaken]);
  };

  // Handle next question button click
  const handleNextQuestion = () => {
    if (currentQuestionIndex < sessionQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      const currentQuestion = sessionQuestions[currentQuestionIndex];
      const isCorrect = selectedAnswer === currentQuestion.answer;
      const sessionData = {
        username,
        prototype: "Timed Random",
        score: score,
        times: times,
        totalTime: times.reduce((a, b) => a + b, 0)
      };
      navigate("/", { state: { sessionData } });
    }
  };

  const currentQuestion = sessionQuestions[currentQuestionIndex];

  return (
    <div style={styles.container}>
      <h2 style={styles.greeting}>
        Hello, {username}! (Timed Random - {difficulty})
      </h2>
      {!quizStarted ? (
        <button style={styles.button} onClick={handleStartQuiz}>
          Start Timed Random Quiz
        </button>
      ) : (
        <div style={styles.quizContainer}>
          <p style={styles.timer}>Time Left: {Math.floor(timer)}s</p>
          <h3 style={styles.question}>{currentQuestion.question}</h3>
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              style={{
                ...styles.optionButton,
                backgroundColor: selectedAnswer === null 
                  ? "#b0c4de" 
                  : option === currentQuestion.answer 
                    ? "#90ee90" // Light green for correct answer
                    : selectedAnswer === option 
                      ? option === currentQuestion.answer ? "#90ee90" : "#ffcccb" // Red if selected wrong
                      : "#b0c4de",
              }}
              onClick={() => handleAnswer(option)}
              disabled={selectedAnswer !== null}
            >
              {option}
            </button>
          ))}
          {selectedAnswer !== null && (
            <div style={styles.explanation}>
              <p style={styles.explanationTitle}>Explanation:</p>
              <p style={styles.explanationText}>{currentQuestion.explanation}</p>
              <button 
                style={styles.nextButton}
                onClick={handleNextQuestion}
              >
                {currentQuestionIndex < sessionQuestions.length - 1 ? "Next Question" : "Finish Quiz"}
              </button>
            </div>
          )}
          <p style={styles.progress}>
            Question {currentQuestionIndex + 1} of {sessionQuestions.length}
          </p>
          <p style={styles.score}>Score: {score}</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    textAlign: "center",
    backgroundColor: "#e6f0fa",
  },
  greeting: {
    color: "#4682b4",
    fontSize: "2rem",
  },
  timer: {
    color: "#ff6347",
    fontSize: "1.5rem",
  },
  quizContainer: {
    marginTop: "20px",
  },
  question: {
    fontSize: "1.5rem",
    color: "#4682b4",
  },
  optionButton: {
    display: "block",
    width: "80%",
    margin: "10px auto",
    padding: "10px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#b0c4de",
    color: "#333",
    transition: "background-color 0.3s ease",
  },
  progress: {
    marginTop: "20px",
    color: "#4682b4",
  },
  score: {
    fontSize: "1.25rem",
    color: "#4682b4",
    marginTop: "20px",
  },
  button: {
    padding: "10px 20px",
    fontSize: "1.25rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#4682b4",
    color: "#fff",
    marginTop: "20px",
  },
  explanation: {
    backgroundColor: "#f0f8ff", // Alice Blue
    borderRadius: "8px",
    padding: "10px 15px",
    margin: "15px auto",
    width: "80%",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    textAlign: "left",
  },
  explanationTitle: {
    fontWeight: "bold",
    color: "#4682b4",
    marginBottom: "5px",
  },
  explanationText: {
    color: "#333",
    lineHeight: "1.4",
  },
  nextButton: {
    backgroundColor: "#4682b4",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    margin: "10px 0",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "block",
    width: "100%",
  },
};

export default TimedQuizRandomScreen;
